class RepairWizard extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-repair-form]');
    this.brand = this.querySelector('[data-repair-brand]');
    this.deviceStep = this.querySelector('[data-repair-device-step]');
    this.dateStep = this.querySelector('[data-repair-date-step]');
    this.purchaseDate = this.querySelector('[data-repair-purchase-date]');
    this.purchaseDay = this.querySelector('[data-repair-purchase-day]');
    this.purchaseMonth = this.querySelector('[data-repair-purchase-month]');
    this.purchaseYear = this.querySelector('[data-repair-purchase-year]');
    this.evaluateButton = this.querySelector('[data-repair-evaluate]');
    this.registrationStep = this.querySelector('[data-repair-registration-step]');
    this.partnerResult = this.querySelector('[data-repair-partner-result]');
    this.quantisResult = this.querySelector('[data-repair-quantis-result]');
    this.details = this.querySelector('[data-repair-details]');
    this.modelSearch = this.querySelector('[data-repair-model-search]');
    this.modelList = this.querySelector('[data-repair-model-list]');
    this.modelError = this.querySelector('[data-repair-model-error]');
    this.serialPhotoField = this.querySelector('[data-repair-serial-photo-field]');
    this.serialPhoto = this.querySelector('[data-repair-serial-photo]');
    this.certificateField = this.querySelector('[data-repair-certificate-field]');
    this.certificate = this.querySelector('[data-repair-certificate]');
    this.noReceipt = this.querySelector('[data-repair-no-receipt]');
    this.outsideWarrantyField = this.querySelector('[data-repair-outside-warranty-field]');
    this.complaintPhotoField = this.querySelector('[data-repair-complaint-photo-field]');
    this.complaintPhoto = this.querySelector('[data-repair-complaint-photo]');
    this.summary = this.querySelector('[data-repair-summary]');
    this.attachmentList = this.querySelector('[data-repair-attachment-list]');
    this.timestamp = this.querySelector('[data-repair-timestamp]');
    this.submitButton = this.querySelector('[data-repair-submit]');
    this.submitMessage = this.querySelector('[data-repair-submit-message]');
    this.models = Object.fromEntries(
      Object.entries(JSON.parse(this.querySelector('[data-repair-model-data]').textContent)).map(([brand, models]) => [
        brand,
        models.map((model) => model.trim()).filter(Boolean),
      ])
    );
    this.serialPhotoModels = this.parseLines(this.dataset.serialPhotoModels);
    this.currentRoute = '';
    this.currentModels = [];
    this.baseOutsideWarranty = false;
    this.isExtendedWarranty = false;
    this.userInteracted = false;
    this.submitting = false;
    this.submitCompletionTimer = null;

    this.timestamp.value = Date.now();
    this.addEventListener('pointerdown', () => {
      this.userInteracted = true;
    }, { once: true });
    this.addEventListener('keydown', () => {
      this.userInteracted = true;
    }, { once: true });

    this.brand.addEventListener('change', () => this.handleBrandChange());
    this.querySelectorAll('[name="apparaatsoort"]').forEach((input) => {
      input.addEventListener('change', () => this.handleDeviceChange());
    });
    this.purchaseDay.addEventListener('change', () => this.syncPurchaseDate());
    this.purchaseMonth.addEventListener('change', () => {
      this.updateDayOptions();
      this.syncPurchaseDate();
    });
    this.purchaseYear.addEventListener('change', () => {
      this.updateDayOptions();
      this.syncPurchaseDate();
    });
    this.evaluateButton.addEventListener('click', () => this.evaluate());
    this.querySelectorAll('[name="verlengde_garantie_geregistreerd"]').forEach((input) => {
      input.addEventListener('change', () => this.handleRegistration(input.value));
    });
    this.modelSearch.addEventListener('input', () => this.handleModelInput());
    this.noReceipt.addEventListener('change', () => {
      this.updateReceiptChoice();
    });
    this.querySelectorAll('[name="klacht_zichtbaar"]').forEach((input) => {
      input.addEventListener('change', () => this.toggleComplaintPhoto(input.value === 'Ja'));
    });
    this.form.addEventListener('submit', (event) => this.handleSubmission(event));
    this.updateReceiptChoice();
    this.updateAttachmentList();
  }

  parseLines(value) {
    return (value || '')
      .split('\n')
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);
  }

  today() {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    return new Date(now.getTime() - offset * 60000).toISOString().split('T')[0];
  }

  selectedDevice() {
    return this.querySelector('[name="apparaatsoort"]:checked')?.value || '';
  }

  handleBrandChange() {
    const requiresDevice = ['Nokia', 'Thomson'].includes(this.brand.value);
    this.querySelectorAll('[name="apparaatsoort"]').forEach((input) => {
      input.required = requiresDevice;
    });
    this.toggle(this.deviceStep, !requiresDevice);
    this.clearRadios('apparaatsoort');
    this.toggle(this.dateStep, requiresDevice || !this.brand.value);
    this.clearPurchaseDate();
    this.resetOutcome();
  }

  handleDeviceChange() {
    this.toggle(this.dateStep, false);
    this.clearPurchaseDate();
    this.resetOutcome();
  }

  clearPurchaseDate() {
    this.purchaseDay.value = '';
    this.purchaseMonth.value = '';
    this.purchaseYear.value = '';
    this.purchaseDate.value = '';
    this.updateDayOptions();
  }

  updateDayOptions() {
    const selectedDay = Number(this.purchaseDay.value);
    const month = Number(this.purchaseMonth.value);
    const year = Number(this.purchaseYear.value) || new Date().getFullYear();
    const daysInMonth = month ? new Date(year, month, 0).getDate() : 31;

    this.purchaseDay.querySelectorAll('option:not(:first-child)').forEach((option) => {
      option.hidden = Number(option.value) > daysInMonth;
      option.disabled = Number(option.value) > daysInMonth;
    });

    if (selectedDay > daysInMonth) this.purchaseDay.value = '';
  }

  syncPurchaseDate() {
    const day = Number(this.purchaseDay.value);
    const month = Number(this.purchaseMonth.value);
    const year = Number(this.purchaseYear.value);

    this.purchaseDate.value =
      day && month && year
        ? `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
        : '';
    this.resetOutcome();
  }

  validatePurchaseDate() {
    for (const field of [this.purchaseDay, this.purchaseMonth, this.purchaseYear]) {
      if (!field.value) {
        field.reportValidity();
        return false;
      }
    }

    if (this.purchaseDate.value > this.today()) {
      this.purchaseDay.setCustomValidity('De aankoopdatum kan niet in de toekomst liggen.');
      this.purchaseDay.reportValidity();
      this.purchaseDay.setCustomValidity('');
      return false;
    }

    return true;
  }

  resetOutcome() {
    this.currentRoute = '';
    this.baseOutsideWarranty = false;
    this.isExtendedWarranty = false;
    this.toggle(this.registrationStep, true);
    this.clearRadios('verlengde_garantie_geregistreerd');
    this.toggle(this.partnerResult, true);
    this.toggle(this.quantisResult, true);
    this.toggle(this.details, true);
    this.certificate.required = false;
    this.certificate.checked = false;
    this.toggleSerialPhoto(false);
    this.toggleComplaintPhoto(false);
  }

  evaluate() {
    if (!this.brand.value) {
      this.brand.reportValidity();
      return;
    }

    if (['Nokia', 'Thomson'].includes(this.brand.value) && !this.selectedDevice()) {
      this.querySelector('[name="apparaatsoort"]').reportValidity();
      return;
    }

    if (!this.validatePurchaseDate()) return;

    const brand = this.brand.value;
    const device = this.selectedDevice();

    if (!['Nokia', 'Thomson'].includes(brand) || device === 'Streaming device') {
      this.showQuantis();
      return;
    }

    const age = this.ageInYears(this.purchaseDate.value);
    if (brand === 'Nokia' && age > 2 && age <= 4) {
      this.toggle(this.registrationStep, false);
      this.registrationStep.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (brand === 'Nokia') {
      this.showQuantis();
      return;
    }

    this.showPartner(age <= 2 ? 'fabrieksgarantie' : 'buiten_garantie');
  }

  ageInYears(dateValue) {
    const purchase = new Date(`${dateValue}T12:00:00`);
    const today = new Date(`${this.today()}T12:00:00`);
    let age = today.getFullYear() - purchase.getFullYear();
    const anniversaryPassed =
      today.getMonth() > purchase.getMonth() ||
      (today.getMonth() === purchase.getMonth() && today.getDate() >= purchase.getDate());
    if (!anniversaryPassed) age -= 1;

    const anniversary = new Date(purchase);
    anniversary.setFullYear(purchase.getFullYear() + age);
    const nextAnniversary = new Date(anniversary);
    nextAnniversary.setFullYear(anniversary.getFullYear() + 1);
    return age + (today - anniversary) / (nextAnniversary - anniversary);
  }

  handleRegistration(value) {
    if (value === 'Ja') {
      this.showQuantis(true);
    } else {
      this.showQuantis();
    }
  }

  showPartner(reason) {
    this.currentRoute = 'Tech Repair';
    this.toggle(this.quantisResult, true);
    this.toggle(this.details, true);
    this.toggle(this.partnerResult, false);
    this.querySelector('[data-repair-route]').value = this.currentRoute;
    this.querySelector('[data-repair-partner-reason]').value = reason;
    this.partnerResult.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  showQuantis(requiresCertificate = false) {
    this.currentRoute = 'Quantis Electronics';
    this.isExtendedWarranty = requiresCertificate;
    this.baseOutsideWarranty = !requiresCertificate && this.ageInYears(this.purchaseDate.value) > 2;
    this.toggle(this.partnerResult, true);
    this.toggle(this.quantisResult, false);
    this.toggle(this.details, false);
    this.toggle(this.certificateField, !requiresCertificate);
    this.certificate.required = requiresCertificate;
    if (!requiresCertificate) this.certificate.checked = false;
    this.querySelector('[data-repair-route]').value = this.currentRoute;
    this.querySelector('[data-repair-selected-brand]').value = this.brand.value;
    this.querySelector('[data-repair-partner-reason]').value = '';
    this.querySelector('[data-repair-selected-device]').value = this.selectedDevice() || 'Overig';
    this.querySelector('[data-repair-selected-date]').value = this.purchaseDate.value;
    this.prepareModels();
    this.updateReceiptChoice();
    this.updateSummary();
    this.updateAttachmentList();
    this.quantisResult.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  prepareModels() {
    this.currentModels = this.models[this.brand.value] || [];
    this.modelList.innerHTML = '';
    this.currentModels.forEach((model) => {
      const option = document.createElement('option');
      option.value = model;
      this.modelList.appendChild(option);
    });
    this.modelSearch.value = '';
    this.modelSearch.placeholder = this.currentModels.length
      ? 'Typ een deel van het typenummer, bijvoorbeeld 5300'
      : 'Vul het volledige typenummer in';
    this.modelSearch.setAttribute('aria-describedby', this.currentModels.length ? `${this.modelError.id} ${this.modelSearch.id}-hint` : this.modelError.id);
    this.modelSearch.dataset.requireMatch = this.currentModels.length ? 'true' : 'false';
    this.modelError.hidden = true;
    this.toggleSerialPhoto(false);
  }

  handleModelInput() {
    this.modelError.hidden = true;
    const normalized = this.modelSearch.value.trim().toLowerCase();
    const selectedModel = this.currentModels.find((model) => model.toLowerCase() === normalized);
    this.toggleSerialPhoto(Boolean(selectedModel && this.serialPhotoModels.includes(normalized)));
  }

  toggleSerialPhoto(show) {
    this.toggle(this.serialPhotoField, !show);
    this.serialPhoto.required = show;
    if (!show) this.serialPhoto.checked = false;
    this.updateAttachmentList();
  }

  toggleComplaintPhoto(show) {
    this.toggle(this.complaintPhotoField, !show);
    this.complaintPhoto.required = show;
    if (!show) this.complaintPhoto.checked = false;
    this.updateAttachmentList();
  }

  updateReceiptChoice() {
    const isOutsideWarranty =
      !this.isExtendedWarranty && (this.baseOutsideWarranty || this.noReceipt.checked);
    this.toggle(this.outsideWarrantyField, !isOutsideWarranty);
    if (this.currentRoute === 'Quantis Electronics' && this.brand.value === 'Nokia') this.updateSummary();
    this.updateAttachmentList();
  }

  nokiaWarrantyStatus() {
    if (this.isExtendedWarranty) return 'Verlengde garantie (geldig 4-jaarscertificaat meesturen)';
    return this.baseOutsideWarranty || this.noReceipt.checked ? 'Buiten garantie' : 'Normale garantie';
  }

  updateSummary() {
    const device = this.selectedDevice();
    const parts = [this.brand.value];
    if (device) parts.push(device);
    parts.push(`aangekocht op ${this.purchaseDate.value.split('-').reverse().join('-')}`);
    if (this.brand.value === 'Nokia') parts.push(this.nokiaWarrantyStatus());
    this.summary.textContent = parts.join(' - ');
  }

  validateSubmission() {
    const model = this.modelSearch.value.trim().toLowerCase();
    const requiresMatch = this.modelSearch.dataset.requireMatch === 'true';
    const exactMatch = this.currentModels.some((item) => item.toLowerCase() === model);
    const honeypot = this.querySelector('[data-repair-honeypot]').value.trim();

    if (requiresMatch && !exactMatch) {
      this.modelError.hidden = false;
      this.modelSearch.focus();
      return false;
    }

    if (honeypot || !this.userInteracted || Date.now() - Number(this.timestamp.value) < 8000) {
      this.showSubmitMessage(
        'Het formulier kon nog niet worden verzonden. Controleer de gegevens en probeer het over enkele seconden opnieuw.',
        'error'
      );
      return false;
    }

    return true;
  }

  handleSubmission(event) {
    event.preventDefault();
    if (this.submitting || !this.validateSubmission()) return;

    this.submitting = true;
    this.classList.add('is-submitting');
    this.submitButton.disabled = true;
    this.submitButton.textContent = 'E-mail wordt geopend...';
    this.submitMessage.hidden = true;

    window.location.href = this.mailtoUrl();
    this.finishSubmissionState();
    this.submitButton.disabled = false;
    this.submitButton.textContent = 'E-mail met reparatiegegevens opnieuw openen';
    this.showSubmitMessage(
      'Als je e-mailprogramma is geopend, voeg dan de gevraagde bijlage(n) toe en verstuur de e-mail. Opent er niets? Stuur de gevraagde gegevens en bijlage(n) zelf naar service@quantiselectronics.com.',
      'success'
    );
    this.submitMessage.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  mailtoUrl() {
    const subject = this.dataset.emailSubject || 'Nieuwe reparatieaanvraag via website';
    const serviceEmail = this.dataset.serviceEmail || 'service@quantiselectronics.com';
    const body = this.emailBody();
    return `mailto:${encodeURIComponent(serviceEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  emailBody() {
    const rows = [
      ['Afhandeling', this.valueFor('afhandeling')],
      ['Merk', this.valueFor('merk')],
      ['Apparaatsoort', this.valueFor('apparaatsoort')],
      ['Aankoopdatum', this.formatDate(this.valueFor('aankoopdatum'))],
      ['Typenummer', this.valueFor('typenummer')],
      ['Serienummer', this.valueFor('serienummer')],
      ['Klacht zichtbaar op apparaat', this.valueFor('klacht_zichtbaar')],
      ['Klachtomschrijving', this.valueFor('klachtomschrijving')],
      ['Naam', this.valueFor('naam')],
      ['E-mailadres', this.valueFor('email')],
      ['Telefoonnummer', this.valueFor('telefoonnummer')],
      ['Straat en huisnummer', this.valueFor('straat')],
      ['Postcode', this.valueFor('postcode')],
      ['Plaats', this.valueFor('plaats')],
      ['Geen aankoopnota of factuur aanwezig', this.checkedValue('geen_aankoopnota_aanwezig')],
      ['Garantiecertificaat wordt meegestuurd', this.checkedValue('garantie_certificaat_meesturen')],
      ['Foto serienummersticker wordt meegestuurd', this.checkedValue('serienummer_foto_meesturen')],
      ['Foto klacht wordt meegestuurd', this.checkedValue('klacht_foto_meesturen')],
      ['Akkoord reparatievoorwaarden', this.checkedValue('akkoord_reparatievoorwaarden')],
    ];

    if (this.brand.value === 'Nokia') rows.splice(1, 0, ['Garantiestatus', this.nokiaWarrantyStatus()]);

    const attachmentLines = this.attachmentInstructions();
    const outsideWarranty = !this.isExtendedWarranty && (this.baseOutsideWarranty || this.noReceipt.checked);

    return [
      'Nieuwe reparatieaanvraag via www.quantiselectronics.com',
      '',
      ...rows.map(([label, value]) => `${label}: ${value || '-'}`),
      '',
      'Bijlagen toevoegen:',
      ...attachmentLines.map((line) => `- ${line}`),
      '',
      outsideWarranty
        ? 'Let op: voor herstel onder garantie is een aankoopnota of factuur verplicht. Wanneer geen aankoopnota of factuur wordt meegestuurd, of wanneer daaruit blijkt dat de garantieperiode is verstreken, kunnen onderzoekskosten van EUR 40 inclusief btw worden berekend.'
        : '',
      '',
      `Reparatievoorwaarden: ${this.dataset.termsUrl || ''}`,
      `Privacyverklaring: ${this.dataset.privacyUrl || ''}`,
    ].filter((line, index, lines) => line || lines[index - 1] !== '').join('\n');
  }

  attachmentInstructions() {
    const instructions = [];
    if (!this.noReceipt.checked) instructions.push('Aankoopnota of factuur');
    if (this.certificate.required) instructions.push('Garantiecertificaat');
    if (this.serialPhoto.required) instructions.push('Foto van de serienummersticker');
    if (this.complaintPhoto.required) instructions.push('Foto waarop de klacht zichtbaar is');
    if (!instructions.length) instructions.push('Geen verplichte bijlagen volgens de gemaakte keuzes');
    return instructions;
  }

  updateAttachmentList() {
    if (!this.attachmentList) return;
    this.attachmentList.innerHTML = '';
    this.attachmentInstructions().forEach((instruction) => {
      const item = document.createElement('li');
      item.textContent = instruction;
      this.attachmentList.appendChild(item);
    });
  }

  valueFor(name) {
    const field = this.form.elements[name];
    if (!field) return '';
    if (field instanceof RadioNodeList) {
      return field.value || '';
    }
    if (field.type === 'checkbox') {
      return field.checked ? field.value : '';
    }
    return field.value || '';
  }

  checkedValue(name) {
    return this.valueFor(name) || 'Nee';
  }

  formatDate(value) {
    return value ? value.split('-').reverse().join('-') : '';
  }

  finishSubmissionState() {
    this.submitting = false;
    this.classList.remove('is-submitting');
    if (this.submitCompletionTimer) {
      window.clearTimeout(this.submitCompletionTimer);
      this.submitCompletionTimer = null;
    }
  }

  showSubmitMessage(message, type) {
    this.submitMessage.textContent = message;
    this.submitMessage.className = `repair-wizard__submit-message repair-wizard__submit-message--${type}`;
    this.submitMessage.hidden = false;
  }

  clearRadios(name) {
    this.querySelectorAll(`[name="${name}"]`).forEach((input) => {
      input.checked = false;
    });
  }

  toggle(element, hidden) {
    element.toggleAttribute('data-repair-hidden', hidden);
  }
}

if (!customElements.get('repair-wizard')) {
  customElements.define('repair-wizard', RepairWizard);
}
